import { type AskPunttAgentStep } from "@mg/schemas/src/christo/askPuntt";
import {
  Brain,
  CaretDown,
  CaretRight,
  CheckCircle,
  CircleNotch,
  ListNumbers,
} from "@phosphor-icons/react";
import { Button, Flex, Text } from "@radix-ui/themes";
import { useEffect, useMemo, useRef, useState } from "react";

interface AgentStepsProps {
  steps: AskPunttAgentStep[];
  isStreaming: boolean;
}

// ─── Step pair / group types ─────────────────────────────────────────────────

type StepPair = {
  call: AskPunttAgentStep & { type: "tool_call" };
  result?: AskPunttAgentStep & { type: "tool_result" };
};

type StepGroup = {
  tool: string;
  displayName: string;
  statusName?: string;
  pairs: StepPair[];
  /** Timestamp of first call — stable key across re-renders */
  firstTimestamp: string;
};

type GroupedItem = StepGroup | (AskPunttAgentStep & { type: "thinking" });

function isStepGroup(item: GroupedItem): item is StepGroup {
  return "pairs" in item;
}

/**
 * Groups consecutive same-tool call+result pairs into StepGroups.
 * Thinking steps and different tools break the group.
 */
function groupConsecutiveToolSteps(steps: AskPunttAgentStep[]): GroupedItem[] {
  const result: GroupedItem[] = [];
  let i = 0;

  while (i < steps.length) {
    const step = steps[i];

    if (step.type === "thinking") {
      result.push(step as AskPunttAgentStep & { type: "thinking" });
      i++;
      continue;
    }

    if (step.type === "tool_call") {
      const group: StepGroup = {
        tool: step.tool ?? "",
        displayName: step.displayName ?? "",
        statusName: step.statusName,
        pairs: [],
        firstTimestamp: step.timestamp,
      };

      while (i < steps.length) {
        const current = steps[i];
        if (current.type === "tool_call" && current.tool === step.tool) {
          const pair: StepPair = {
            call: current as AskPunttAgentStep & { type: "tool_call" },
          };
          i++;
          if (i < steps.length && steps[i].type === "tool_result") {
            pair.result = steps[i] as AskPunttAgentStep & {
              type: "tool_result";
            };
            i++;
          }
          group.pairs.push(pair);
        } else {
          break;
        }
      }

      result.push(group);
      continue;
    }

    // Orphan tool_result — skip gracefully
    i++;
  }

  return result;
}

// ─── Individual step renderers ────────────────────────────────────────────────

function ThinkingStep({
  step,
}: {
  step: AskPunttAgentStep & { type: "thinking" };
}) {
  const preview =
    step.content.length > 120
      ? `${step.content.slice(0, 120)}...`
      : step.content;

  return (
    <Flex
      align="start"
      gap="2"
      className="px-2 py-1"
      data-testid="agent-step"
      data-step-type="thinking"
    >
      <Brain size={14} className="mt-0.5 shrink-0 text-puntt-neutral-gray-9" />
      <Text size="2" className="italic text-puntt-neutral-gray-9">
        {preview}
      </Text>
    </Flex>
  );
}

function ToolCallStep({
  step,
}: {
  step: AskPunttAgentStep & { type: "tool_call" };
}) {
  const primaryName = step.statusName || step.displayName;
  const showOriginal = !!step.statusName;
  const secondaryText = step.description
    ? step.description
    : step.args
      ? JSON.stringify(step.args).slice(0, 80)
      : "";

  return (
    <Flex
      align="center"
      gap="2"
      className="px-2 py-1"
      data-testid="agent-step"
      data-step-type="tool_call"
    >
      <CircleNotch
        size={14}
        className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
      />
      <Text size="2" weight="medium" className="text-puntt-neutral-gray-11">
        {primaryName}
      </Text>
      {showOriginal && (
        <Text size="1" className="text-puntt-neutral-gray-8">
          {step.displayName}
        </Text>
      )}
      {secondaryText && (
        <Text size="2" className="truncate text-puntt-neutral-gray-9">
          {secondaryText}
        </Text>
      )}
    </Flex>
  );
}

function CreateTicketProgressStep({
  step,
}: {
  step: AskPunttAgentStep & { type: "tool_call" };
}) {
  const startMs = useMemo(
    () => new Date(step.timestamp).getTime(),
    [step.timestamp],
  );
  const [elapsed, setElapsed] = useState(() =>
    Math.floor((Date.now() - startMs) / 1000),
  );

  useEffect(() => {
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startMs) / 1000));
    }, 500);
    return () => clearInterval(id);
  }, [startMs]);

  const pct = Math.min(Math.round((elapsed / 10) * 85), 85);
  const primaryName = step.statusName || step.displayName;

  return (
    <Flex
      direction="column"
      gap="1"
      className="px-2 py-1"
      data-testid="agent-step"
      data-step-type="tool_call"
    >
      <Flex align="center" gap="2">
        <CircleNotch
          size={14}
          className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
        />
        <Text size="2" className="font-medium text-puntt-neutral-gray-11">
          {primaryName}
        </Text>
        <Text
          size="1"
          className="ml-auto tabular-nums text-puntt-neutral-gray-8"
        >
          {elapsed}s
        </Text>
      </Flex>
      <div className="ml-4 h-1 overflow-hidden rounded-full bg-puntt-neutral-gray-3">
        <div
          className="h-full rounded-full bg-puntt-accent-7 transition-[width] duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </Flex>
  );
}

function ToolResultStep({
  step,
}: {
  step: AskPunttAgentStep & { type: "tool_result" };
}) {
  const summary =
    step.summary.length > 100
      ? `${step.summary.slice(0, 100)}...`
      : step.summary;
  const primaryName = step.statusName || step.displayName;

  return (
    <Flex
      align="center"
      gap="2"
      className="px-2 py-1"
      data-testid="agent-step"
      data-step-type="tool_result"
    >
      <CheckCircle
        size={14}
        weight="fill"
        className="shrink-0 text-malachite-700"
      />
      <Text size="2" weight="medium" className="text-puntt-neutral-gray-11">
        {primaryName}
      </Text>
      {step.resultCount !== undefined && step.resultCount > 0 && (
        <Text size="2" className="text-puntt-neutral-gray-9">
          {step.resultCount} {step.resultCount === 1 ? "result" : "results"}
        </Text>
      )}
      {summary && (
        <Text size="2" className="truncate text-puntt-neutral-gray-9">
          {summary}
        </Text>
      )}
    </Flex>
  );
}

// ─── Collapsed group row ──────────────────────────────────────────────────────

function CollapsedToolGroup({
  group,
  isNew,
  streamingEndedAt,
}: {
  group: StepGroup;
  isNew: boolean;
  streamingEndedAt: number | null;
}) {
  const [expanded, setExpanded] = useState(false);

  // Auto-collapse when streaming ends
  useEffect(() => {
    if (streamingEndedAt !== null) setExpanded(false);
  }, [streamingEndedAt]);

  const callCount = group.pairs.length;
  const primaryName = group.statusName || group.displayName;
  const lastPair = group.pairs[group.pairs.length - 1];
  const allDone = !!lastPair?.result;

  // Build summary label: show first description/query if available
  const firstCall = group.pairs[0]?.call;
  const firstQuery = firstCall?.description
    ? firstCall.description.slice(0, 30)
    : firstCall?.args
      ? JSON.stringify(firstCall.args).slice(0, 30)
      : null;

  const label =
    callCount === 1
      ? primaryName
      : firstQuery
        ? `${primaryName} — "${firstQuery}" + ${callCount - 1} more`
        : `${primaryName} (${callCount} times)`;

  if (callCount === 1) {
    // Single pair — render exactly as before, no collapse chrome
    return (
      <div
        data-testid="agent-step-group"
        className={isNew ? "animate-block-fade-up" : ""}
      >
        {allDone ? (
          <ToolResultStep step={lastPair.result!} />
        ) : lastPair.call.tool === "create_ticket" ? (
          <CreateTicketProgressStep step={lastPair.call} />
        ) : (
          <ToolCallStep step={lastPair.call} />
        )}
      </div>
    );
  }

  // Multiple same-tool pairs — collapsed summary with expand toggle
  return (
    <div
      data-testid="agent-step-group"
      className={isNew ? "animate-block-fade-up" : ""}
    >
      <button
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center gap-2 rounded px-2 py-1 text-left hover:bg-puntt-neutral-gray-3"
        aria-expanded={expanded}
      >
        {allDone ? (
          <CheckCircle
            size={14}
            weight="fill"
            className="shrink-0 text-malachite-700"
          />
        ) : (
          <CircleNotch
            size={14}
            className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
          />
        )}
        <Text
          size="2"
          className="flex-1 truncate font-medium text-puntt-neutral-gray-11"
        >
          {label}
        </Text>
        {expanded ? (
          <CaretDown size={10} className="shrink-0 text-puntt-neutral-gray-9" />
        ) : (
          <CaretRight
            size={10}
            className="shrink-0 text-puntt-neutral-gray-9"
          />
        )}
      </button>

      <div
        className={`overflow-hidden transition-[max-height,opacity] duration-200 ${
          expanded ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="ml-4 space-y-0.5 border-l border-puntt-neutral-gray-3 pl-2">
          {group.pairs.map((pair) => (
            <div key={pair.call.timestamp}>
              {pair.result ? (
                <ToolResultStep step={pair.result} />
              ) : pair.call.tool === "create_ticket" ? (
                <CreateTicketProgressStep step={pair.call} />
              ) : (
                <ToolCallStep step={pair.call} />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main AgentSteps component ────────────────────────────────────────────────

export function AgentSteps({ steps, isStreaming }: AgentStepsProps) {
  const [manualToggle, setManualToggle] = useState<boolean | null>(null);
  const prevGroupCountRef = useRef(0);
  const wasStreamingRef = useRef(isStreaming);

  const expanded = manualToggle !== null ? manualToggle : isStreaming;

  const groups = useMemo(() => groupConsecutiveToolSteps(steps), [steps]);

  const prevGroupCount = prevGroupCountRef.current;
  useEffect(() => {
    prevGroupCountRef.current = groups.length;
  }, [groups.length]);

  // Signal per-group components to auto-collapse when streaming ends
  const [streamingEndedAt, setStreamingEndedAt] = useState<number | null>(null);
  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming) {
      setStreamingEndedAt(Date.now());
    }
    wasStreamingRef.current = isStreaming;
  }, [isStreaming]);

  const stepCount = steps.length;
  const summaryText = isStreaming
    ? "Working..."
    : `${stepCount} Step${stepCount === 1 ? "" : "s"}`;

  return (
    <div data-testid="agent-steps">
      {isStreaming ? (
        <div className="rounded-xl border border-puntt-neutral-gray-4 bg-base-white p-3 shadow-sm">
          <Flex
            align="center"
            gap="2"
            className="mb-3 border-b border-puntt-neutral-gray-4 pb-2"
          >
            <CircleNotch
              size={16}
              className="animate-spin text-puntt-accent-9 motion-reduce:animate-none"
            />
            <Text
              size="2"
              weight="medium"
              className="text-puntt-neutral-gray-12"
            >
              Working
            </Text>
          </Flex>
          <div className="ml-3 space-y-0.5 border-l-2 border-puntt-accent-6/30 pl-3">
            {groups.map((group, idx) => {
              const isNew = idx >= prevGroupCount;
              if (!isStepGroup(group)) {
                return (
                  <div
                    key={group.timestamp}
                    className={isNew ? "animate-block-fade-up" : ""}
                  >
                    <ThinkingStep step={group} />
                  </div>
                );
              }
              return (
                <CollapsedToolGroup
                  key={group.firstTimestamp}
                  group={group}
                  isNew={isNew}
                  streamingEndedAt={streamingEndedAt}
                />
              );
            })}
          </div>
        </div>
      ) : (
        <>
          <Button
            size="1"
            variant="outline"
            color="gray"
            className="cursor-pointer"
            onClick={() => setManualToggle(!expanded)}
            aria-expanded={expanded}
            aria-label={`${expanded ? "Collapse" : "Expand"} agent steps`}
          >
            <ListNumbers size={14} />
            {summaryText}
            {expanded ? <CaretDown size={10} /> : <CaretRight size={10} />}
          </Button>

          <div
            className={`overflow-hidden transition-[max-height,opacity] ${
              expanded
                ? "max-h-[600px] opacity-100 duration-300"
                : "max-h-0 opacity-0 duration-200"
            }`}
          >
            <div className="ml-3 space-y-0.5 border-l-2 border-puntt-accent-6/30 pl-3">
              {groups.map((group, idx) => {
                const isNew = idx >= prevGroupCount;
                if (!isStepGroup(group)) {
                  return (
                    <div
                      key={group.timestamp}
                      className={isNew ? "animate-block-fade-up" : ""}
                    >
                      <ThinkingStep step={group} />
                    </div>
                  );
                }
                return (
                  <CollapsedToolGroup
                    key={group.firstTimestamp}
                    group={group}
                    isNew={isNew}
                    streamingEndedAt={streamingEndedAt}
                  />
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
